import Container from '@/components/Container';

import DefaultContentTemplateTextEditor from '@/components/DefaultContentTemplate/TextEditor';
import LessonCard from '@/components/Learn/Lesson';
import DefaultContentTemplateAuthorDetailed from '@/components/DefaultContentTemplate/AuthorDetailed';

import { fetchCourseBySlug, fetchRelatedContent, getPage, normalizeLearnItems } from '@/services/learn/course.service';
import { useEffect, useState } from 'react';
import {
  type ComponentCommonAuthorDetailed,
  type Course,
  type CoursePage as CoursePageEntity,
  type Learn,
  GENERATED,
} from '@edwin-group/people-graphql';
import { Link, useParams } from 'react-router-dom';
import CourseSkeleton from './Course.skeleton';
import { fetchCourseLearn } from '@/services/learn/learn.service';
import { getAuthors, getStartLesson } from '@/utils/learn';
import { NavigationSkeleton } from '@/components/ui/navigation';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import { BackButton } from '@/components/ui/back-button';

import Highlight from '@/components/Highlight';
import Divider from '@/components/Divider';
import { ComponentCommonTextEditorWithHtml } from '@/components/DefaultContentTemplate';
import CourseCard from '@/components/Card/Course';
import { trackGAEventOnceExpiresIn5Minutes } from '@/lib/ga-custom-events/dispatcher';

function CoursePage() {
  const [cx, setCourse] = useState<Course | null>(null);
  const [learn, setLearn] = useState<Learn | null>(null);
  const [relatedContent, setRelatedContent] = useState<Array<Course> | null>(null);
  const [page, setPage] = useState<CoursePageEntity | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const { slug } = useParams();

  useEffect(() => {
    (async () => {
      if (!slug) return;
      setLoading(true);

      const [courseResult, relatedContentResult, learnResult, pageReult] = await Promise.all([
        fetchCourseBySlug(slug),
        fetchRelatedContent(slug),
        fetchCourseLearn(slug),
        getPage(),
      ]);

      setCourse(courseResult);
      trackGAEventOnceExpiresIn5Minutes(
        'course_opened',
        {
          course_document_id: courseResult?.documentId ?? '',
          course_name: courseResult?.title ?? courseResult?.attributes?.title ?? '',
        },
        [courseResult?.documentId ?? slug ?? ''],
      );
      setLearn(learnResult as unknown as Learn);
      setPage(pageReult.page);
      setRelatedContent(relatedContentResult);
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <>
        <NavigationSkeleton />
        <CourseSkeleton />
      </>
    );
  }

  const c = cx?.attributes;
  const normalizedLearnItems = normalizeLearnItems(learn);

  if (c === null) {
    return null;
  }

  const authors = getAuthors(c);
  const startLesson = getStartLesson(c!, learn);
  let startLessonlabel = 'Begin';

  if (learn && learn?.attributes?.completed) {
    startLessonlabel = 'Watch again';
  }

  if (learn && !learn?.attributes?.completed) {
    startLessonlabel = 'Continue';
  }

  return (
    <>
      <Container className="default-content-template min-h-40 !gap-0">
        <div className="col-span-12">
          {learn?.attributes?.completed && c?.courseCompletedSection?.title && c?.courseCompletedSection?.message ? (
            <AnimatedFadeInComponent>
              <div className="mb-sh-30 flex flex-col items-center rounded-2xl border border-people-green-100 bg-boxes-background p-5">
                <h4 className="flex items-center font-haffer text-sh-28-32 font-bold">
                  {c?.courseCompletedSection?.title}
                </h4>
                <p className="font-haffersq text-sh-sm">{c?.courseCompletedSection?.message}</p>
              </div>
            </AnimatedFadeInComponent>
          ) : null}
        </div>
        <div className="col-span-12">
          {cx ? (
            <AnimatedFadeInComponent>
              <Highlight
                title={cx?.title ?? c?.title ?? ''}
                sectionTitle="Courses"
                description={c?.description ?? ''}
                video={c?.hero?.data?.attributes?.mime?.includes('video') ? c?.hero?.data?.attributes?.url : undefined}
                image={c?.hero?.data?.attributes?.mime?.includes('image') ? c?.hero?.data?.attributes?.url : undefined}
                imageAlt={cx?.title ?? c?.title ?? ''}
                showTitleWithActions={true}
                titleWithActionsProps={{
                  entity: cx,
                  entityId: cx?.documentId ?? '0',
                  category: GENERATED.Enum_Bookmark_Category?.Course,
                  like: false,
                }}
                backButton={<BackButton to="/learn" />}
                button={
                  <>
                    {startLesson ? (
                      <AnimatedFadeInComponent>
                        <Link
                          to={`/learn/course/${c?.slug}/module/${startLesson?.idModule}/lesson/${startLesson?.idLesson}`}
                          className="font-haffer text-sh-12-18 font-bold text-dark-green">
                          {startLessonlabel}
                        </Link>
                      </AnimatedFadeInComponent>
                    ) : null}
                  </>
                }
              />
            </AnimatedFadeInComponent>
          ) : null}
        </div>

        <div className="col-span-8">
          <h1 className="my-sh-30 font-haffer text-sh-30-38 font-bold">Course introduction</h1>
          {c?.textContent?.markdownText ? (
            <AnimatedFadeInComponent>
              <DefaultContentTemplateTextEditor
                component={
                  {
                    id: 'textContent',
                    markdownText: c?.textContent?.markdownText || '',
                    markdownHtml: c?.textContent?.markdownText || '',
                  } as ComponentCommonTextEditorWithHtml
                }
                className="!my-0 font-haffersq text-dark-grey [&_*]:!font-haffersq [&_*]:!text-dark-grey"
              />
            </AnimatedFadeInComponent>
          ) : null}

          {authors?.length ? (
            <AnimatedFadeInComponent>
              <DefaultContentTemplateAuthorDetailed
                variant="learn"
                component={
                  {
                    id: 'DefaultContentTemplateAuthorDetailed',
                    authors: {
                      data: authors,
                    },
                  } as unknown as ComponentCommonAuthorDetailed
                }
              />
            </AnimatedFadeInComponent>
          ) : null}
        </div>
        <div className="col-span-12">
          {c?.modules?.map((m, idx) => (
            <AnimatedFadeInComponent key={`${m?.title}-${idx}`}>
              <div key={`${m?.title}-${idx}`} className="mt-sh-60 grid grid-cols-12">
                <p className="col-span-12 mb-5 font-haffer text-sh-28-32 font-bold">
                  Module {idx + 1}: {m?.title}
                </p>
                <div className="col-span-8">
                  <p className="mb-5 mt-0 font-haffersq text-sh-16-24 text-dark-grey">{m?.description}</p>
                </div>
                <div className="col-span-12 flex flex-col gap-sh-30">
                  {m?.lessons?.map((lesson, lessonIdx) => {
                    const doneLearnItem = !!normalizedLearnItems?.find(
                      (learnItem) =>
                        learnItem?.idModule === m?.moduleStableId && learnItem?.idLesson === lesson?.lessonStableId,
                    );
                    return (
                      <LessonCard
                        title={`Lesson ${lessonIdx + 1}: ${lesson?.title || ''}`}
                        description={lesson?.description || ''}
                        thumbnail={lesson!.thumbnail}
                        key={lesson?.lessonStableId}
                        slug={lesson?.id}
                        done={doneLearnItem}
                        watchLabel={page?.lesson?.beginButtonLabel || undefined}
                        watchAgainLabel={page?.lesson?.watchAgainButtonlabel || undefined}
                        lockedLabel={page?.lesson?.lockedButtonlabel || undefined}
                        tag="Lesson"
                        duration={lesson?.duration || ''}
                        url={`/learn/course/${c?.slug}/module/${m?.moduleStableId}/lesson/${lesson?.lessonStableId}`}
                      />
                    );
                  })}
                </div>
              </div>
            </AnimatedFadeInComponent>
          ))}
        </div>

        {(relatedContent || []).length > 0 ? (
          <div className="col-span-12">
            <Divider isBody />
            <div className="mt-sh-60">
              <h3 className="mb-6 font-haffer text-sh-md font-bold">You might enjoy</h3>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {relatedContent!.map((course) => (
                  <CourseCard course={course} key={course.documentId} />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </Container>
    </>
  );
}

export default CoursePage;
